<script setup lang="ts">
import { computed } from 'vue'
import { Bar, Pie } from 'vue-chartjs'
import { Chart as ChartJS, Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale, ArcElement } from 'chart.js'
import type { Meeting } from '@/stores/appStore'

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement)

const props = defineProps<{
  meetings: Meeting[]
}>()

const meetingsByDay = computed(() => {
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
  const distribution = new Array(7).fill(0)

  for (const meeting of props.meetings) {
    const day = new Date(meeting.start).getDay()
    distribution[day]++
  }

  return {
    labels: days,
    datasets: [
      {
        label: 'Meetings per Day',
        backgroundColor: '#7e3af2',
        data: distribution
      }
    ]
  }
})

const meetingDurationBreakdown = computed(() => {
  const durations = {
    '0-30min': 0,
    '30-60min': 0,
    '1-2hrs': 0,
    '2+ hrs': 0
  }

  for (const meeting of props.meetings) {
    const duration = (new Date(meeting.end).getTime() - new Date(meeting.start).getTime()) / (1000 * 60) // in minutes

    if (duration <= 30) durations['0-30min']++
    else if (duration <= 60) durations['30-60min']++
    else if (duration <= 120) durations['1-2hrs']++
    else durations['2+ hrs']++
  }

  return {
    labels: Object.keys(durations),
    datasets: [
      {
        backgroundColor: ['#C147E1', '#7447e1', '#4767E1', '#D47EE1'],
        data: Object.values(durations)
      }
    ]
  }
})

const barOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false
    }
  },
  scales: {
    y: {
      ticks: {
        color: '#fff'
      },
      grid: {
        color: 'rgba(255, 255, 255, 0.1)'
      }
    },
    x: {
      ticks: {
        color: '#fff'
      },
      grid: {
        color: 'rgba(255, 255, 255, 0.1)'
      }
    }
  }
}

const pieOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'right' as const,
      labels: {
        color: '#fff'
      }
    }
  }
}
</script>

<template>
  <div class="row q-col-gutter-md full-width">
    <div class="col-12 col-md paddingFixerSingle">
      <div class="myCard q-pa-md">
        <div class="text-h5 text-white q-mb-md">Meeting Distribution</div>
        <div style="height: 300px">
          <Bar :data="meetingsByDay" :options="barOptions" />
        </div>
      </div>
    </div>

    <div class="col-12 col-md paddingFixerSingle">
      <div class="myCard q-pa-md">
        <div class="text-h5 text-white q-mb-md">Meeting Duration Breakdown</div>
        <div style="height: 300px">
          <Pie :data="meetingDurationBreakdown" :options="pieOptions" />
        </div>
      </div>
    </div>
  </div>
</template>
